import { ChangeDetectionStrategy, Component, Input, OnDestroy, forwardRef } from '@angular/core';
import { RelatedProductsComponentDef } from '@df/catalog-ui/related-products/related-products.component.def';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuTrackService } from '@df/module-klevu-search/klevu-track.service';
import { NgInject } from '@df/ng/ng-inject';
import { VisibilityService } from '@df/ui/common/visibility.service';
import { Subscription } from 'rxjs';
import { SwiperModule } from 'swiper/angular';
import { GtmImpressionTrackDirective } from '../../../module/gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from '../../../module/gtm/gtm-track-product-click.directive';
import { ProductConfigurableOptionsStockService } from '../product/configurable-options/stock/product-configurable-options-stock.service';
import { ProductComponent } from '../product/product.component';
/**
 * Sample usage
 *
 * ```html
 *     <related-products class="block m-t m-b-8"
 *          [source]="model"
 *          [options]="{limit: [2,3,4,4][app.client.sizeId], engines: 'category'}">
 *              You may also like
 *     </related-products>
 * ```
 */
@Component({
    selector: 'related-products',
    templateUrl: './related-products.html',
    styleUrls: ['./related-products.scss'],
    standalone: true,
    imports: [SwiperModule, forwardRef(() => ProductComponent), GtmImpressionTrackDirective, GtmTrackProductClickDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RelatedProductsComponent extends RelatedProductsComponentDef implements OnDestroy {
    stockStatus = new Status();
    @NgInject(App)
    protected app: App;
    @NgInject(ProductConfigurableOptionsStockService)
    protected productConfigurableOptionsStockService: ProductConfigurableOptionsStockService;
    @NgInject(KlevuTrackService)
    private klevuTrackService: KlevuTrackService;
    @NgInject(VisibilityService)
    private visibilityService: VisibilityService;
    @Input()
    klevuRecommendationId?: string;
    @Input()
    listId = 'related-products';
    @Input()
    listName = 'Related Products';
    @Input()
    listTrackOff = false;
    @Input()
    acClickCode?: string;
    private isKlevuTrackEventSent = false;
    private inViewSubscriptions: Subscription;
    override ngOnDestroy(): void {
        this.inViewSubscriptions?.unsubscribe();
        super.ngOnDestroy();
    }
    protected override onModelsChange() {
        super.onModelsChange();
        if (this.client.isCms && this.listing?.models?.length) {
            this.preloadProductStock();
        }
        this.isKlevuTrackEventSent = false;
        this.inViewSubscriptions?.unsubscribe();
        this.inViewSubscriptions = this.visibilityService.isInView(this.elementRef.nativeElement).subscribe(inView => {
            if (inView) {
                this.trackKlevu();
            }
        });
    }
    private async preloadProductStock(): Promise<void> {
        const skus: string[] = [];
        await Promise.all(
            this.listing.models.map(async (model: Product<IProductDataV2>) => {
                const sku = await this.loadDirectChildren(model);
                skus.push(...sku);
            })
        );
        await this.productConfigurableOptionsStockService.loadStocks(skus);
    }
    private loadDirectChildren(model: Product<IProductDataV2>): Promise<string[]> {
        return new Promise(resolve => {
            this.app.request.send(
                undefined,
                () => this.app.resources.product.loadAllIds(model.parent.directChildrenIds, 2, { safe: true }),
                () => {
                    const productSkus = model.parent.directChildren.map(i => i.data?.sku);
                    resolve(productSkus);
                },
                resolve
            );
        });
    }
    private get isKlevu(): boolean {
        return KlevuSearch.isKlevuEngine(this.options?.engines);
    }
    private trackKlevu(): void {
        if (this.isKlevu && !this.isKlevuTrackEventSent) {
            this.isKlevuTrackEventSent = true;
            const products = this.listing?.getSourceModels() as Product<IProductDataV2>[];
            if (products?.length) {
                const klevuRecommendationId = this.klevuRecommendationId;
                if (klevuRecommendationId) {
                    this.klevuTrackService.trackRecommendation({
                        eventListId: klevuRecommendationId,
                        event: KlevuSearch.EAnalyticsRecommendationEvent.view,
                        products: products.map((product, index) => {
                            return {
                                model: product,
                                position: index
                            };
                        })
                    });
                }
            }
        }
    }
}
